/**
 * SkinDefinitions — card skin (art image) definitions shared by backend and
 * frontend.
 *
 * A skin is an alternative image for a minion class (minion type). Every
 * class has exactly ONE default skin — the image the card has always used
 * (`/card-art/<Class>.png`). Default skins are NEVER locked: they are
 * available to every player, including guests and bots (who can only ever
 * use defaults because they have no account-backed skin selections).
 *
 * Additional skins are "locked": a player cannot select them until the skin
 * is unlocked for their account (per-user unlock rows in the user database,
 * see UserDatabase skin_unlocks). Every locked skin has an unlock path —
 * either an ACHIEVEMENT (see ACHIEVEMENT_SKIN_REWARDS in AchievementService)
 * or a DIRECT game condition (see CONDITIONAL_REWARD_SKIN_IDS) — both are
 * tracked authoritatively by the DevServer and land through the single
 * UserDatabase.unlockSkin entry point.
 *
 * ADDING A NEW CLASS: append a default skin (and optionally locked skins)
 * for the new type below. Everything downstream is data-driven:
 *  - the server's SkinsResponse is built from this list;
 *  - the frontend skins overlay renders one section per class found in the
 *    response (ordered via MINION_TYPE_*), so a new class appears with no
 *    UI code changes;
 *  - card rendering resolves class → selected skin ID → image through
 *    resolveSkinImage, falling back to the class default.
 *
 * ADDING A NEW SKIN: append an entry here. It shows up in the overlay
 * automatically (locked or not) and can be selected as soon as it is
 * unlocked for the user.
 */

import { MinionType, VALID_MINION_TYPES } from '../models/MinionType';

/**
 * A single card skin (art image) for a minion class.
 */
export interface SkinDefinition {
  /**
   * Stable skin ID (e.g. 'mage_archmage'). Convention: `<class>_<variant>`.
   * This is what players select and what is stored/persisted — never the
   * image path.
   */
  id: string;
  /** The minion class this skin applies to. */
  minionType: MinionType;
  /** Human-readable display name shown in the skins overlay. */
  name: string;
  /**
   * Image URL served by the dev server (files in `public/card-art/`).
   * `/card-art/skins/` holds the non-default art.
   */
  imageUrl: string;
  /**
   * Locked skins are not selectable until unlocked for the player
   * (UserDatabase.unlockSkin). Default skins are never locked.
   */
  locked: boolean;
  /** True for the class's default skin (the historically used image). */
  isDefault: boolean;
  /**
   * Human-readable unlock criteria shown as small text beneath the skin
   * in the skins overlay (LOCKED skins only). Achievement reward skins
   * derive their criteria from the linked achievement definition (server
   * side), so they don't carry this field; condition skins state it here.
   */
  unlockCriteria?: string;
  /**
   * The progress value at which this skin unlocks (condition skins with
   * measurable progress, e.g. "upgrade a Mage to level 6" → 6). Absent
   * for one-shot conditions and achievement reward skins (their progress
   * lives in the achievement_progress table). The server persists the
   * per-user progress in the skin_progress table (UserDatabase) and
   * unlocks the skin when it reaches this target.
   */
  unlockTarget?: number;
}

/**
 * The full skin catalogue. Single source of truth — the server seeds its
 * SkinsResponse from this list and validates every selection against it.
 *
 * Order is display order inside each class (defaults first).
 */
export const SKIN_DEFINITIONS: readonly SkinDefinition[] = [
  // ── Defaults (the images cards have always used — always available) ──
  { id: 'warrior_default', minionType: 'warrior', name: 'Warrior', imageUrl: '/card-art/Warrior.png', locked: false, isDefault: true },
  { id: 'mage_default', minionType: 'mage', name: 'Mage', imageUrl: '/card-art/Mage.png', locked: false, isDefault: true },
  { id: 'healer_default', minionType: 'healer', name: 'Healer', imageUrl: '/card-art/Healer.png', locked: false, isDefault: true },
  { id: 'tank_default', minionType: 'tank', name: 'Tank', imageUrl: '/card-art/Tank.png', locked: false, isDefault: true },
  { id: 'bard_default', minionType: 'bard', name: 'Bard', imageUrl: '/card-art/Bard.png', locked: false, isDefault: true },
  { id: 'undead_default', minionType: 'undead', name: 'Undead', imageUrl: '/card-art/Undead.png', locked: false, isDefault: true },
  // Locked (not yet obtainable) classes — default art only, no locked skins
  // yet (the /card-art/skins/ content is pending; the overlay shows a single
  // default entry per class until then).
  { id: 'blightcaster_default', minionType: 'blightcaster', name: 'Blightcaster', imageUrl: '/card-art/Blightcaster.png', locked: false, isDefault: true },
  { id: 'buffer_default', minionType: 'buffer', name: 'Buffer', imageUrl: '/card-art/Buffer.png', locked: false, isDefault: true },
  { id: 'runekeeper_default', minionType: 'runekeeper', name: 'Runekeeper', imageUrl: '/card-art/Runekeeper.png', locked: false, isDefault: true },
  { id: 'swarmer_default', minionType: 'swarmer', name: 'Swarmer', imageUrl: '/card-art/Swarmer.png', locked: false, isDefault: true },
  { id: 'spellmaker_default', minionType: 'spellmaker', name: 'Spellmaker', imageUrl: '/card-art/SpellMaker.png', locked: false, isDefault: true },
  // TASK 204: the Spawner default skin is REMOVED — the accidental
  // `spawner` class (a Swarmer misspelling) no longer exists; the
  // Infestation token IS the Swarmer (swarmer_default art).

  // ── Locked skins (the art files live in /card-art/skins/) ────────────
  // Every entry below has an unlock path (achievement reward or direct
  // game condition) — see ACHIEVEMENT_REWARD_SKIN_IDS /
  // CONDITIONAL_REWARD_SKIN_IDS at the bottom of this file.

  // Warrior_2.png — upgrade a Warrior to level 6 (progress = highest level
  // a Warrior-class minion of the user has ever reached; mirrors the Mage
  // "Archmage" skin).
  { id: 'warrior_archwarrior', minionType: 'warrior', name: 'Archwarrior', imageUrl: '/card-art/skins/Warrior_2.png', locked: true, isDefault: false, unlockCriteria: 'Upgrade a Warrior to level 6.', unlockTarget: 6 },
  // Warrior_3.png — have a Warrior with more than 50 HP (progress =
  // high-water mark of the current HP of one of the user's Warriors;
  // "more than 50" → target 51).
  { id: 'warrior_juggernaut', minionType: 'warrior', name: 'Juggernaut', imageUrl: '/card-art/skins/Warrior_3.png', locked: true, isDefault: false, unlockCriteria: 'Have a Warrior with more than 50 HP.', unlockTarget: 51 },
  // Warrior_4.png — kill 100 Undead-class units with Warrior-class cards
  // (progress = Undead-class units killed by the user's WARRIOR-class
  // minions in combat; Necromancy tokens are excluded — same convention as
  // the kill statistic). The Mage "Bone Collector" skin counts the same
  // kills without the warrior-killer restriction.
  { id: 'warrior_bone_breaker', minionType: 'warrior', name: 'Bone Breaker', imageUrl: '/card-art/skins/Warrior_4.png', locked: true, isDefault: false, unlockCriteria: 'Kill 100 Undead-class units with Warrior-class cards.', unlockTarget: 100 },
  // Undead_2.png — play a total of 50 Undead-class cards from the hand
  // (progress = lifetime count of Undead-class plays from the hand;
  // Necromancy tokens are never played from a hand, so they don't count).
  { id: 'undead_risen_host', minionType: 'undead', name: 'Risen Host', imageUrl: '/card-art/skins/Undead_2.png', locked: true, isDefault: false, unlockCriteria: 'Play a total of 50 Undead-class cards from your hand.', unlockTarget: 50 },
  // Undead_3.png — one-shot: an Undead-class card brought back to life by
  // the RESURRECT ABILITY (the Rune of Rebirth does not count).
  { id: 'undead_reanimated', minionType: 'undead', name: 'Reanimated', imageUrl: '/card-art/skins/Undead_3.png', locked: true, isDefault: false, unlockCriteria: 'Resurrect an Undead-class card with the Resurrect ability.' },

  // ── Mage condition skins (unlocked directly by a game condition — the
  //    DevServer tracks the per-user progress in the skin_progress table
  //    and calls unlockSkin when the unlockTarget is reached) ───────────
  // Mage_2.png — upgrade a Mage to level 6 (progress = highest level a
  // Mage-class minion of the user has ever reached).
  { id: 'mage_archmage', minionType: 'mage', name: 'Archmage', imageUrl: '/card-art/skins/Mage_2.png', locked: true, isDefault: false, unlockCriteria: 'Upgrade a Mage to level 6.', unlockTarget: 6 },
  // Mage_4.png — have 6 Undead-class cards on the battlefield at the same
  // time (progress = high-water mark of simultaneous Undead-class cards).
  { id: 'mage_death_horde', minionType: 'mage', name: 'Death Horde', imageUrl: '/card-art/skins/Mage_4.png', locked: true, isDefault: false, unlockCriteria: 'Have 6 Undead-class cards on your battlefield at the same time.', unlockTarget: 6 },
  // Mage_6.png — kill 100 Undead-class units (progress = Undead-class
  // units the user's minions have killed in combat).
  { id: 'mage_bone_collector', minionType: 'mage', name: 'Bone Collector', imageUrl: '/card-art/skins/Mage_6.png', locked: true, isDefault: false, unlockCriteria: 'Kill 100 Undead-class units.', unlockTarget: 100 },

  // ── Achievement reward skins (unlocked by the matching achievement —
  //    see ACHIEVEMENT_SKIN_REWARDS in AchievementService) ──────────────
  { id: 'bard_music_master', minionType: 'bard', name: 'Music Master', imageUrl: '/card-art/skins/BardMusicMaster.png', locked: true, isDefault: false },
  { id: 'bard_healthy_undead', minionType: 'bard', name: 'Healthy Undead', imageUrl: '/card-art/skins/BardHealthyUndead.png', locked: true, isDefault: false },
  { id: 'bard_healing_voice', minionType: 'bard', name: 'Healing Voice', imageUrl: '/card-art/skins/BardHealingVoice.png', locked: true, isDefault: false },
  { id: 'healer_necromaster', minionType: 'healer', name: 'Necromaster', imageUrl: '/card-art/skins/Healer_3.png', locked: true, isDefault: false },
  { id: 'healer_forgemaster', minionType: 'healer', name: 'Forgemaster', imageUrl: '/card-art/skins/Healer_4.png', locked: true, isDefault: false },
  { id: 'healer_second_chance', minionType: 'healer', name: 'Second Chance', imageUrl: '/card-art/skins/Healer_5.png', locked: true, isDefault: false },
  { id: 'tank_mountain', minionType: 'tank', name: 'Mountain', imageUrl: '/card-art/skins/Tank_2.png', locked: true, isDefault: false },
  // Mage_3.png — reward for the "Exploding Heartbeat" achievement (deal a
  // total of 300 damage with the Explosive ability).
  { id: 'mage_dynamite_heart', minionType: 'mage', name: 'Dynamite Heart', imageUrl: '/card-art/skins/Mage_3.png', locked: true, isDefault: false },
  // Mage_5.png — reward for the "Reaper" achievement (eliminate 100 other
  // players or bots).
  { id: 'mage_headless_horseman', minionType: 'mage', name: 'Headless Horseman', imageUrl: '/card-art/skins/Mage_5.png', locked: true, isDefault: false },
  // Warrior_5.png — reward for the "Private Army" achievement (6 Warrior-
  // class units at level 6 on the battlefield at the same time).
  { id: 'warrior_private_army', minionType: 'warrior', name: 'Private Army', imageUrl: '/card-art/skins/Warrior_5.png', locked: true, isDefault: false },

  // ── Condition reward skins (unlocked directly by a game condition — no
  //    achievement; the DevServer calls unlockSkin when the condition is
  //    met: win a combat round with only Healer-class minions left alive,
  //    or buy both the Rune of the Tank and the Holy Rune in one game) ──
  { id: 'healer_blessed_chorus', minionType: 'healer', name: 'Blessed Chorus', imageUrl: '/card-art/skins/Healer_2.png', locked: true, isDefault: false, unlockCriteria: 'Win a combat round with only Healer-class minions left alive.' },
  { id: 'tank_holy_bulwark', minionType: 'tank', name: 'Holy Bulwark', imageUrl: '/card-art/skins/Tank_3.png', locked: true, isDefault: false, unlockCriteria: 'Buy both the Rune of the Tank and the Holy Rune in the same game.' },

  // ── Blightcaster skins (the locked trio's first skin content) ─────────
  // Blightcaster_1.png — 7-DAY LOGIN STREAK REWARD (direct unlock, no
  // achievement): part of the streak reward pool (STREAK_REWARD_SKIN_POOL)
  // — claiming the 7-day reward grants ONE random skin the user does not
  // own yet from the pool; owning every pool skin means the claim grants
  // nothing.
  { id: 'blightcaster_lucky_streak', minionType: 'blightcaster', name: 'Lucky Streak', imageUrl: '/card-art/skins/Blightcaster_1.png', locked: true, isDefault: false, unlockCriteria: 'Complete a 7-day login streak.' },
  // Blightcaster_2.png — reward for the "Blight" achievement (transform a
  // minion into a Blightcaster).
  { id: 'blightcaster_blight', minionType: 'blightcaster', name: 'The Blight', imageUrl: '/card-art/skins/Blightcaster_2.png', locked: true, isDefault: false },
  // Blightcaster_4.png — one-shot: a BLIGHTCASTER-class card brought back
  // to life by the RESURRECT ABILITY (the Rune of Rebirth does not count —
  // same convention as the Undead "Reanimated" skin).
  { id: 'blightcaster_resurrected', minionType: 'blightcaster', name: 'Undying', imageUrl: '/card-art/skins/Blightcaster_4.png', locked: true, isDefault: false, unlockCriteria: 'Resurrect a Blightcaster with the Resurrect ability.' },
  // Blightcaster_5.png — reward for the "Big Trouble" achievement (4
  // Blightcaster-class minions on the battlefield at the same time).
  { id: 'blightcaster_big_trouble', minionType: 'blightcaster', name: 'Big Trouble', imageUrl: '/card-art/skins/Blightcaster_5.png', locked: true, isDefault: false },
  // Blightcaster_6.png — kill 100 other cards with Blightcaster-class
  // minions (progress = cards killed by the user's BLIGHTCASTER-class
  // minions in combat; same authoritative kill attribution as the kill
  // statistic).
  { id: 'blightcaster_gravemaster', minionType: 'blightcaster', name: 'Gravemaster', imageUrl: '/card-art/skins/Blightcaster_6.png', locked: true, isDefault: false, unlockCriteria: 'Kill 100 other cards with Blightcasters.', unlockTarget: 100 },

  // ── Buffer skins ──────────────────────────────────────────────────────
  // Buffer_1.png — 7-DAY LOGIN STREAK REWARD (pool — see above).
  { id: 'buffer_lucky_streak', minionType: 'buffer', name: 'Lucky Streak', imageUrl: '/card-art/skins/Buffer_1.png', locked: true, isDefault: false, unlockCriteria: 'Complete a 7-day login streak.' },
  // Buffer_2.png — reward for the "Over-powered" achievement (get a
  // Buffer-class minion to level 7 — only reachable with a Grandmaster
  // raising the upgrade cap).
  { id: 'buffer_over_powered', minionType: 'buffer', name: 'Over-powered', imageUrl: '/card-art/skins/Buffer_2.png', locked: true, isDefault: false },

  // ── Runekeeper skins ──────────────────────────────────────────────────
  // RuneKeeper_1.png — 7-DAY LOGIN STREAK REWARD (pool — see above).
  { id: 'runekeeper_lucky_streak', minionType: 'runekeeper', name: 'Lucky Streak', imageUrl: '/card-art/skins/RuneKeeper_1.png', locked: true, isDefault: false, unlockCriteria: 'Complete a 7-day login streak.' },
  // RuneKeeper_3.png — reward for the "Fourclover" achievement (buy a 4th
  // rune — only possible while a RuneKeeper in play raises the per-game
  // rune cap above the base of 3).
  { id: 'runekeeper_fourclover', minionType: 'runekeeper', name: 'Fourclover', imageUrl: '/card-art/skins/RuneKeeper_3.png', locked: true, isDefault: false },
  // RuneKeeper_4.png — kill 100 UNDEAD-class minions with Runekeeper-class
  // minions (progress = Undead-class units killed by the user's
  // RUNEKEEPER-class minions in combat; same authoritative kill
  // attribution as the kill statistic).
  { id: 'runekeeper_gravereaver', minionType: 'runekeeper', name: 'Gravereaver', imageUrl: '/card-art/skins/RuneKeeper_4.png', locked: true, isDefault: false, unlockCriteria: 'Kill 100 Undead-class minions with Runekeepers.', unlockTarget: 100 },
  // RuneKeeper_5.png — one-shot: buy the RUNE OF GREAT FORTUNE as the
  // 4th rune of a game (the cap-raising RuneKeeper in play is what makes
  // a 4th purchase possible at all).
  { id: 'runekeeper_great_fortune', minionType: 'runekeeper', name: 'Great Fortune', imageUrl: '/card-art/skins/RuneKeeper_5.png', locked: true, isDefault: false, unlockCriteria: 'Buy the Rune of Great Fortune as your 4th rune.' },

  // ── Swarmer skins ─────────────────────────────────────────────────────
  // Swarmer_1.png — 7-DAY LOGIN STREAK REWARD (pool — see above).
  { id: 'swarmer_lucky_streak', minionType: 'swarmer', name: 'Lucky Streak', imageUrl: '/card-art/skins/Swarmer_1.png', locked: true, isDefault: false, unlockCriteria: 'Complete a 7-day login streak.' },
  // Swarmer_2.png — 7-DAY LOGIN STREAK REWARD (pool — see above).
  { id: 'swarmer_hive_champion', minionType: 'swarmer', name: 'Hive Champion', imageUrl: '/card-art/skins/Swarmer_2.png', locked: true, isDefault: false, unlockCriteria: 'Complete a 7-day login streak.' },

  // ── Spellmaker skins ──────────────────────────────────────────────────
  // SpellMaker_1.png — 7-DAY LOGIN STREAK REWARD (pool — see above).
  { id: 'spellmaker_lucky_streak', minionType: 'spellmaker', name: 'Lucky Streak', imageUrl: '/card-art/skins/SpellMaker_1.png', locked: true, isDefault: false, unlockCriteria: 'Complete a 7-day login streak.' },
  // SpellMaker_2.png — upgrade a Spellmaker to level 5 (progress = highest
  // level a Spellmaker-class minion of the user has ever reached; the same
  // mirror of the Mage "Archmage" / Warrior "Archwarrior" condition skins).
  { id: 'spellmaker_grand_spellmaker', minionType: 'spellmaker', name: 'Grand Spellmaker', imageUrl: '/card-art/skins/SpellMaker_2.png', locked: true, isDefault: false, unlockCriteria: 'Upgrade a Spellmaker to level 5.', unlockTarget: 5 },
  // SpellMaker_3.png — one-shot: a SPELLMAKER-class card brought back to
  // life by the RESURRECT ABILITY (the Rune of Rebirth does not count —
  // same convention as the Undead "Reanimated" / Blightcaster "Undying"
  // skins).
  { id: 'spellmaker_resurrected', minionType: 'spellmaker', name: 'Resurgent', imageUrl: '/card-art/skins/SpellMaker_3.png', locked: true, isDefault: false, unlockCriteria: 'Resurrect a Spellmaker with the Resurrect ability.' },

  // TASK 204: the Spawner "Spore Brood" locked skin was REMOVED with the
  // spawner class (its 7-day-streak unlock now awards nothing — the skin
  // catalog has no spawner entry anymore).
];

/**
 * Skin IDs of the achievement reward skins. Referenced by the
 * achievement→skin reward map (server side) so the link can never drift
 * from the catalogue above.
 */
export const ACHIEVEMENT_REWARD_SKIN_IDS = {
  /** Reward for the "Music Master" achievement. */
  MUSIC_MASTER: 'bard_music_master',
  /** Reward for the "Healthy Undead" achievement. */
  HEALTHY_UNDEAD: 'bard_healthy_undead',
  /** Reward for the "Healing Voice" achievement. */
  HEALING_VOICE: 'bard_healing_voice',
  /** Reward for the "Necromaster" achievement. */
  NECROMASTER: 'healer_necromaster',
  /** Reward for the "Forgemaster" achievement. */
  FORGEMASTER: 'healer_forgemaster',
  /** Reward for the "Second Chance" achievement. */
  SECOND_CHANCE: 'healer_second_chance',
  /** Reward for the "Mountain" achievement. */
  MOUNTAIN: 'tank_mountain',
  /** Reward for the "Exploding Heartbeat" achievement. */
  EXPLODING_HEARTBEAT: 'mage_dynamite_heart',
  /** Reward for the "Reaper" achievement. */
  REAPER: 'mage_headless_horseman',
  /** Reward for the "Private Army" achievement. */
  PRIVATE_ARMY: 'warrior_private_army',
  /** Reward for the "Blight" achievement (transform a minion into a
   * Blightcaster). */
  BLIGHT: 'blightcaster_blight',
  /** Reward for the "Big Trouble" achievement (4 Blightcasters on the
   * battlefield at the same time). */
  BIG_TROUBLE: 'blightcaster_big_trouble',
  /** Reward for the "Over-powered" achievement (get a Buffer to level 7). */
  OVER_POWERED: 'buffer_over_powered',
  /** Reward for the "Fourclover" achievement (buy a 4th rune). */
  FOURCLOVER: 'runekeeper_fourclover',
} as const;

/**
 * Skin IDs that are unlocked DIRECTLY by a game condition (no achievement
 * stands between the condition and the unlock). The DevServer checks the
 * condition and calls UserDatabase.unlockSkin with the matching ID.
 */
export const CONDITIONAL_REWARD_SKIN_IDS = {
  /**
   * Unlocked by winning a combat round with only Healer-class minions left
   * alive on the winner's board.
   */
  HEALER_ROUND_WIN: 'healer_blessed_chorus',
  /**
   * Unlocked by buying BOTH the Rune of the Tank and the Holy Rune during
   * the same game (per-game purchased runes, reset at match start).
   */
  TANK_DUAL_RUNE: 'tank_holy_bulwark',
  /**
   * Unlocked by upgrading a Mage-class minion to level 6 (progress:
   * highest level ever reached by one of the user's Mages).
   */
  MAGE_LEVEL_SIX: 'mage_archmage',
  /**
   * Unlocked by having 6 Undead-class cards on the battlefield at the same
   * time (progress: high-water mark of simultaneous Undead-class cards).
   */
  MAGE_UNDEAD_BOARD: 'mage_death_horde',
  /**
   * Unlocked by killing 100 Undead-class units in combat (progress: total
   * Undead-class units the user's minions have killed; Necromancy tokens
   * don't count — same convention as the kill statistic).
   */
  MAGE_UNDEAD_KILLS: 'mage_bone_collector',
  /**
   * Unlocked by upgrading a Warrior-class minion to level 6 (progress:
   * highest level ever reached by one of the user's Warriors).
   */
  WARRIOR_LEVEL_SIX: 'warrior_archwarrior',
  /**
   * Unlocked by having a Warrior with more than 50 HP (progress: high-water
   * mark of the current HP of one of the user's Warriors on the
   * battlefield; "more than 50" → target 51).
   */
  WARRIOR_FIFTY_HP: 'warrior_juggernaut',
  /**
   * Unlocked by killing 100 Undead-class units with Warrior-class cards in
   * combat (progress: Undead-class units killed by the user's WARRIOR-class
   * minions; Necromancy tokens don't count — same convention as the kill
   * statistic).
   */
  WARRIOR_UNDEAD_KILLS: 'warrior_bone_breaker',
  /**
   * Unlocked by playing a total of 50 Undead-class cards from the hand
   * (progress: lifetime count of Undead-class plays from the hand).
   */
  UNDEAD_FIFTY_PLAYS: 'undead_risen_host',
  /**
   * One-shot unlock: an Undead-class card brought back to life by the
   * Resurrect ability (the Rune of Rebirth does not count). No measurable
   * progress — the DevServer calls unlockSkin when the condition is met.
   */
  UNDEAD_RESURRECT: 'undead_reanimated',
  /**
   * One-shot unlock via the 7-day login streak claim: the DevServer picks
   * ONE random skin the user does not own yet from STREAK_REWARD_SKIN_POOL
   * and unlocks it when the claim succeeds. No measurable progress.
   */
  BLIGHTCASTER_LUCKY_STREAK: 'blightcaster_lucky_streak',
  BUFFER_LUCKY_STREAK: 'buffer_lucky_streak',
  RUNEKEEPER_LUCKY_STREAK: 'runekeeper_lucky_streak',
  /**
   * One-shot unlock via the 7-day login streak claim (Swarmer_1.png —
   * pool entry, see BLIGHTCASTER_LUCKY_STREAK).
   */
  SWARMER_LUCKY_STREAK: 'swarmer_lucky_streak',
  /**
   * One-shot unlock via the 7-day login streak claim (Swarmer_2.png —
   * pool entry, see BLIGHTCASTER_LUCKY_STREAK).
   */
  SWARMER_HIVE_CHAMPION: 'swarmer_hive_champion',
  /**
   * One-shot unlock: a BLIGHTCASTER-class card brought back to life by the
   * Resurrect ability (the Rune of Rebirth does not count — same
   * convention as UNDEAD_RESURRECT).
   */
  BLIGHTCASTER_RESURRECT: 'blightcaster_resurrected',
  /**
   * Unlocked by upgrading a Spellmaker-class minion to level 5 (progress:
   * highest level ever reached by one of the user's Spellmakers — the same
   * mirror of MAGE_LEVEL_SIX / WARRIOR_LEVEL_SIX).
   */
  SPELLMAKER_LEVEL_FIVE: 'spellmaker_grand_spellmaker',
  /**
   * One-shot unlock: a SPELLMAKER-class card brought back to life by the
   * Resurrect ability (the Rune of Rebirth does not count — same
   * convention as UNDEAD_RESURRECT / BLIGHTCASTER_RESURRECT).
   */
  SPELLMAKER_RESURRECT: 'spellmaker_resurrected',
  /**
   * One-shot unlock via the 7-day login streak claim (SpellMaker_1.png —
   * pool entry, see BLIGHTCASTER_LUCKY_STREAK).
   */
  SPELLMAKER_LUCKY_STREAK: 'spellmaker_lucky_streak',
  /**
   * Unlocked by killing 100 other cards with Blightcaster-class minions
   * (progress: cards the user's BLIGHTCASTER-class minions killed in
   * combat — the authoritative kill attribution, the same source as kill
   * gold).
   */
  BLIGHTCASTER_HUNDRED_KILLS: 'blightcaster_gravemaster',
  /**
   * Unlocked by killing 100 UNDEAD-class minions with Runekeeper-class
   * minions (progress: Undead-class units the user's RUNEKEEPER-class
   * minions killed in combat).
   */
  RUNEKEEPER_UNDEAD_KILLS: 'runekeeper_gravereaver',
  /**
   * One-shot unlock: buying the Rune of Great Fortune as the 4th rune of
   * a game (purchasedRunes is per-game — the 4th slot only exists while a
   * RuneKeeper in play raises the cap).
   */
  RUNEKEEPER_GREAT_FORTUNE: 'runekeeper_great_fortune',
} as const;

/**
 * The 7-DAY LOGIN STREAK REWARD POOL (skin IDs): claiming the streak
 * reward grants the user ONE random skin from this pool that they do NOT
 * already own. When the user owns every pool skin, the claim succeeds but
 * grants nothing ("no chance of getting a duplicate" — the candidates are
 * computed from the user's unlock rows at claim time).
 *
 * The DevServer is the single writer (via UserDatabase.unlockSkin after a
 * successful claimLoginStreak); the helper below is shared so server and
 * any future client-side preview can never disagree on the pool contents.
 */
export const STREAK_REWARD_SKIN_POOL: readonly string[] = [
  CONDITIONAL_REWARD_SKIN_IDS.BLIGHTCASTER_LUCKY_STREAK,
  CONDITIONAL_REWARD_SKIN_IDS.BUFFER_LUCKY_STREAK,
  CONDITIONAL_REWARD_SKIN_IDS.RUNEKEEPER_LUCKY_STREAK,
  CONDITIONAL_REWARD_SKIN_IDS.SWARMER_LUCKY_STREAK,
  CONDITIONAL_REWARD_SKIN_IDS.SWARMER_HIVE_CHAMPION,
  CONDITIONAL_REWARD_SKIN_IDS.SPELLMAKER_LUCKY_STREAK,
];

/**
 * The streak-reward candidates for a user: the pool minus the skins the
 * user already unlocked (pool order preserved). Empty when the user owns
 * the whole pool — the claim then grants nothing.
 */
export function getStreakRewardCandidates(unlockedSkinIds: Iterable<string>): string[] {
  const owned = new Set(unlockedSkinIds);
  return STREAK_REWARD_SKIN_POOL.filter((id) => !owned.has(id));
}

// ─── Lookup helpers (shared — same resolution on server and client) ───────

/** Index of all skins by ID (built once). */
const SKINS_BY_ID: ReadonlyMap<string, SkinDefinition> = new Map(
  SKIN_DEFINITIONS.map((s) => [s.id, s])
);

/**
 * Returns the skin with the given ID, or null when unknown.
 */
export function getSkinById(skinId: string): SkinDefinition | null {
  return SKINS_BY_ID.get(skinId) ?? null;
}

/**
 * Returns all skins for a class in catalogue order (default first).
 * Unknown classes get an empty list.
 */
export function getSkinsForClass(minionType: string): SkinDefinition[] {
  return SKIN_DEFINITIONS.filter((s) => s.minionType === minionType);
}

/**
 * Returns the default skin for a class, or null when the class has none
 * (unknown/future class without a skin entry).
 */
export function getDefaultSkinForClass(minionType: string): SkinDefinition | null {
  return SKIN_DEFINITIONS.find((s) => s.minionType === minionType && s.isDefault) ?? null;
}

/**
 * True when `skinId` is a known skin that belongs to `minionType`.
 */
export function isSkinForClass(skinId: string, minionType: string): boolean {
  const skin = SKINS_BY_ID.get(skinId);
  return !!skin && skin.minionType === minionType;
}

/**
 * Orders the classes to display for a set of classes present in the data:
 * known classes in their canonical order (VALID_MINION_TYPES) first, then
 * any unknown (future) classes in first-seen order. This is what makes the
 * skins overlay fully dynamic — a brand-new class appears with its own
 * section without any UI code change.
 */
export function orderSkinClasses(presentClasses: Iterable<string>): string[] {
  const present = new Set(presentClasses);
  const ordered: string[] = [];
  for (const t of VALID_MINION_TYPES) {
    if (present.has(t)) ordered.push(t);
  }
  for (const t of present) {
    if (!ordered.includes(t)) ordered.push(t);
  }
  return ordered;
}

/**
 * Resolves the image URL to render for a minion class given a player's
 * selected skin ID for that class.
 *
 * Resolution order:
 *  1. `skinId` names a known skin for this class → that skin's image;
 *  2. otherwise (unknown/absent/foreign-class skin ID, or the skin is a
 *     locked one the player does not own — the server never sends those,
 *     but the client must not crash) → the class's DEFAULT skin image;
 *  3. the class has no skin at all (e.g. the 'unknown' fallback card type)
 *     → undefined (the card keeps its plain background, as before).
 *
 * The client is a pure renderer: it trusts the server's per-player skin
 * selections (authoritative) and only guards against malformed data.
 */
export function resolveSkinImage(minionType: string, skinId?: string | null): string | undefined {
  if (skinId) {
    const skin = SKINS_BY_ID.get(skinId);
    if (skin && skin.minionType === minionType) {
      return skin.imageUrl;
    }
  }
  return getDefaultSkinForClass(minionType)?.imageUrl;
}
